// Cyber Invaders rules, kept free of React and the DOM so they can be tested
// on their own: wave content, scoring, lives, the learning retry and the
// decision tally. engine.ts moves and draws the ships; Invaders.tsx joins the
// two to the arcade cabinet.

export type Category = "file" | "domain" | "action";

export interface ShipDef {
  /** Unique within its wave. */
  id: string;
  label: string;
  risky: boolean;
  category: Category;
  /** One sentence shown when this ship is handled wrongly. */
  lesson: string;
}

export interface WaveDef {
  /** 1-based wave number. */
  n: number;
  category: Category;
  /** QuestionBox title during play. */
  title: string;
  /** QuestionBox subtitle during play. */
  subtitle: string;
  /** Plain-language rule for the wave intro. */
  rule: string;
  /** Ships per row; rows enter one after the other. */
  perRow: number;
  /** Descent speed in logical units per second (playfield is 1280 wide). */
  speed: number;
  ships: ShipDef[];
}

export const MAX_SHIELDS = 3;
export const HIT_RISKY = 100;
export const HIT_SAFE = -50;
export const COMBO_STEP = 25;
/** The streak bonus stops growing after this many steps (+100). */
export const COMBO_MAX_STEPS = 4;
export const WAVE_BONUS = 300;
export const PASS_MARK = 0.75;

export const CATEGORY_LESSON: Record<Category, { topic: string; lesson: string }> = {
  file: {
    topic: "Dangerous file types",
    lesson:
      "Check the last extension: double extensions, scripts (.js, .bat, .vbs, .scr) and macro files (.docm) run code when opened.",
  },
  domain: {
    topic: "Lookalike domains",
    lesson:
      "Compare the whole domain with the one you trust: a swapped character, an extra letter or a trusted name in front of another domain means a different site.",
  },
  action: {
    topic: "Unexpected sign-in requests",
    lesson:
      "A sign-in request you did not start is a warning sign: deny, report or verify it instead of approving or sharing anything.",
  },
};

type Item = [label: string, lesson: string];

const FILES_RISKY: Item[] = [
  ["invoice.pdf.exe", "The last extension is .exe: a program dressed up as a PDF."],
  ["payslip.scr", ".scr is a screensaver file, and Windows runs it as a program."],
  ["update.js", "A .js file from an email runs as a script on Windows, not in the browser."],
  ["report.docm", "The m in .docm means the document carries macros that can run code."],
  ["setup.bat", ".bat is a batch script that runs commands as soon as it is opened."],
  ["photo.jpg.vbs", "The last extension is .vbs, a Visual Basic script, not a photo."],
];

const FILES_SAFE: Item[] = [
  ["report.pdf", "A single .pdf extension is an ordinary document."],
  ["budget.xlsx", ".xlsx is a normal spreadsheet without macros (macro files end in .xlsm)."],
  ["notes.txt", "A .txt file is plain text and cannot run code."],
  ["photo.jpg", "A single .jpg extension is an ordinary image."],
  ["slides.pptx", ".pptx is a normal presentation without macros."],
  ["minutes.docx", ".docx is a normal document without macros (compare .docm)."],
];

export const TRUSTED_DOMAINS = ["nova.com", "bank.ai", "run24.us", "atlas.io"];

const DOMAIN_DECOYS: Item[] = [
  ["n0va.com", "A zero replaces the letter o: n0va, not nova."],
  ["nova.co", "It ends in .co, not .com, so it is a different domain."],
  ["nova-com.net", "The domain is nova-com.net, not nova.com."],
  ["bank-ai.com", "bank-ai.com is a different domain from bank.ai."],
  ["banc.ai", "banc with a c, not bank."],
  ["run24.us.co", "It ends in .us.co, so it belongs to us.co, not run24.us."],
  ["runn24.us", "An extra n: runn24, not run24."],
  ["atlas.io.login-check.com", "The real domain is login-check.com; atlas.io is only a decoy at the front."],
  ["atlass.io", "An extra s: atlass, not atlas."],
  ["at1as.io", "The digit 1 replaces the letter l."],
  ["nova.com.verify-id.net", "The real domain is verify-id.net; nova.com is only a prefix."],
  ["bank.ai-secure.com", "The real domain is ai-secure.com, not bank.ai."],
];

const ACTIONS_RISKY: Item[] = [
  ["APPROVE", "Approving a sign-in you did not start lets the attacker in."],
  ["ALLOW", "Allowing an unexpected request gives the attacker the access they asked for."],
  ["ACCEPT", "Accepting an unknown request or permission hands over access."],
  ["YES", "Saying yes to a prompt you did not expect confirms the attacker's sign-in."],
  ["SHARE", "Sharing a code or file on request gives it to whoever is asking."],
  ["SEND", "Sending a code or password is exactly what the attacker wants."],
  ["POST", "Posting a code or a screenshot of it exposes it to everyone."],
  ["REVEAL", "Revealing a code or password hands the attacker the last piece they need."],
];

const ACTIONS_SAFE: Item[] = [
  ["DENY", "Denying an unexpected sign-in request blocks the attacker."],
  ["REPORT", "Reporting the request lets the security team react."],
  ["VERIFY", "Verifying through a channel you already know is the safe response."],
  ["CANCEL", "Cancelling stops a request you did not start."],
  ["CALL BACK", "Calling back on a number you already know confirms who is asking."],
  ["CHECK URL", "Checking the address before signing in catches fake pages."],
  ["ASK IT", "Asking IT is a safe way to check an odd request."],
  ["LOCK", "Locking your account or screen protects it while you check."],
];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function ships(category: Category, risky: boolean, items: Item[]): ShipDef[] {
  return items.map(([label, lesson]) => ({ id: `${category[0]}-${slug(label)}`, label, risky, category, lesson }));
}

export function shuffle<T>(arr: T[], rng: () => number = Math.random): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** The three waves, ships shuffled. */
export function buildWaves(rng: () => number = Math.random): WaveDef[] {
  const trusted: ShipDef[] = [];
  for (const d of TRUSTED_DOMAINS) {
    for (let k = 1; k <= 3; k++) {
      trusted.push({
        id: `d-${slug(d)}-${k}`,
        label: d,
        risky: false,
        category: "domain",
        lesson: `${d} is on the trusted list. Let it pass.`,
      });
    }
  }
  return [
    {
      n: 1,
      category: "file",
      title: "SHOOT THE DANGEROUS FILES",
      subtitle: "Files that can run code when opened. Let ordinary documents pass.",
      rule: "Look at the last extension. Programs, scripts and macro documents are risky; ordinary documents, images and spreadsheets are safe.",
      perRow: 4,
      speed: 18,
      ships: shuffle([...ships("file", true, FILES_RISKY), ...ships("file", false, FILES_SAFE)], rng),
    },
    {
      n: 2,
      category: "domain",
      title: "SHOOT THE FAKE DOMAINS",
      subtitle: `TRUSTED: ${TRUSTED_DOMAINS.join("  ")}`,
      rule: "Only these four domains are real. Shoot every ship whose domain is not exactly one of them.",
      perRow: 3,
      speed: 17,
      ships: shuffle([...trusted, ...ships("domain", true, DOMAIN_DECOYS)], rng),
    },
    {
      n: 3,
      category: "action",
      title: "LOGIN REQUESTS",
      subtitle: "An unexpected sign-in prompt appears: shoot every action that gives the attacker what they want.",
      rule: "You did not try to sign in. Shoot the actions that give the attacker access or a code; let the careful responses pass.",
      perRow: 4,
      speed: 19,
      ships: shuffle([...ships("action", true, ACTIONS_RISKY), ...ships("action", false, ACTIONS_SAFE)], rng),
    },
  ];
}

export type OutcomeKind = "hit-risky" | "hit-safe" | "missed-risky" | "passed-safe";

export interface Decision {
  id: string;
  wave: number;
  attempt: number;
  label: string;
  category: Category;
  risky: boolean;
  action: "shot" | "passed";
  correct: boolean;
  /** Score change before the floor at 0 is applied. */
  points: number;
}

export interface Outcome {
  kind: OutcomeKind;
  correct: boolean;
  points: number;
  /** Streak bonus included in points (0 when none). */
  comboBonus: number;
  shieldLost: boolean;
  shieldsOut: boolean;
}

export interface WaveResult {
  wave: number;
  correct: number;
  total: number;
  clean: boolean;
  bonus: number;
}

export type Rank = "SPACE CADET" | "CYBER DEFENDER" | "GALACTIC GUARDIAN";

export function rankFor(accuracy: number): Rank {
  const pct = Math.round(accuracy * 100);
  if (pct >= 85) return "GALACTIC GUARDIAN";
  if (pct >= 60) return "CYBER DEFENDER";
  return "SPACE CADET";
}

export function passes(accuracy: number): boolean {
  return accuracy + 1e-9 >= PASS_MARK;
}

export interface Summary {
  score: number;
  correct: number;
  incorrect: number;
  total: number;
  accuracy: number;
  rank: Rank;
  passed: boolean;
  retries: number;
  /** Ships handled wrongly in any attempt, one per label. */
  missed: ShipDef[];
  /** One takeaway per category with mistakes, for the course debrief. */
  mistakes: { topic: string; lesson: string }[];
}

/**
 * One play-through. Every ship yields exactly one decision per attempt: shot,
 * or passed when it reaches the line. Only completed waves count toward the
 * tally; a wave that ran out of lives is replayed and its first attempt is
 * dropped, but what was missed there still appears in the review list.
 */
export class InvadersGame {
  readonly waves: WaveDef[];
  waveIdx = 0;
  score = 0;
  shields = MAX_SHIELDS;
  combo = 0;
  attempt = 1;
  retries = 0;
  readonly committed: Decision[] = [];
  private current: Decision[] = [];
  private resolved = new Set<string>();
  private waveStartScore = 0;
  private missedMap = new Map<string, ShipDef>();

  constructor(waves: WaveDef[] = buildWaves()) {
    this.waves = waves;
  }

  get wave(): WaveDef | undefined {
    return this.waves[this.waveIdx];
  }

  get done(): boolean {
    return this.waveIdx >= this.waves.length;
  }

  /** Decisions so far in the running attempt. */
  get attemptDecisions(): readonly Decision[] {
    return this.current;
  }

  private resetWave() {
    this.score = this.waveStartScore;
    this.shields = MAX_SHIELDS;
    this.combo = 0;
    this.current = [];
    this.resolved.clear();
  }

  startWave() {
    this.waveStartScore = this.score;
    this.attempt = 1;
    this.resetWave();
  }

  /** Learning retry after the lives ran out: same wave, full lives, earlier waves untouched. */
  retryWave() {
    this.attempt += 1;
    this.retries += 1;
    this.resetWave();
  }

  private apply(def: ShipDef, action: "shot" | "passed"): Outcome | null {
    const wave = this.wave;
    if (!wave || this.shields <= 0 || this.resolved.has(def.id)) return null;
    this.resolved.add(def.id);
    let kind: OutcomeKind;
    let points = 0;
    let comboBonus = 0;
    let shieldLost = false;
    if (action === "shot" && def.risky) {
      kind = "hit-risky";
      comboBonus = Math.min(this.combo, COMBO_MAX_STEPS) * COMBO_STEP;
      points = HIT_RISKY + comboBonus;
      this.combo += 1;
    } else if (action === "shot") {
      kind = "hit-safe";
      points = HIT_SAFE;
      shieldLost = true;
      this.combo = 0;
    } else if (def.risky) {
      kind = "missed-risky";
      shieldLost = true;
      this.combo = 0;
    } else {
      kind = "passed-safe";
    }
    const correct = kind === "hit-risky" || kind === "passed-safe";
    this.score = Math.max(0, this.score + points);
    if (shieldLost) this.shields = Math.max(0, this.shields - 1);
    if (!correct && !this.missedMap.has(def.label)) this.missedMap.set(def.label, def);
    this.current.push({
      id: `w${wave.n}-${def.id}`,
      wave: wave.n,
      attempt: this.attempt,
      label: def.label,
      category: def.category,
      risky: def.risky,
      action,
      correct,
      points,
    });
    return { kind, correct, points, comboBonus, shieldLost, shieldsOut: this.shields <= 0 };
  }

  shoot(def: ShipDef): Outcome | null {
    return this.apply(def, "shot");
  }

  pass(def: ShipDef): Outcome | null {
    return this.apply(def, "passed");
  }

  /** Close the running wave: commit its decisions and award the clean-wave bonus. */
  finishWave(): WaveResult | null {
    const wave = this.wave;
    if (!wave) return null;
    const correct = this.current.filter((d) => d.correct).length;
    const clean = this.current.length > 0 && correct === this.current.length;
    const bonus = clean ? WAVE_BONUS : 0;
    this.score += bonus;
    this.committed.push(...this.current);
    const total = this.current.length;
    this.current = [];
    this.resolved.clear();
    this.waveIdx += 1;
    return { wave: wave.n, correct, total, clean, bonus };
  }

  summary(): Summary {
    return this.tally(this.committed, this.committed.length);
  }

  /**
   * Tally for a run that stopped before the last wave (out of lives, or left):
   * every ship of every wave counts, so ships never handled count as missed.
   */
  partialSummary(): Summary {
    const total = this.waves.reduce((n, w) => n + w.ships.length, 0);
    return this.tally([...this.committed, ...this.current], total);
  }

  private tally(decisions: readonly Decision[], total: number): Summary {
    const correct = decisions.filter((d) => d.correct).length;
    const accuracy = total ? correct / total : 0;
    const missed = Array.from(this.missedMap.values());
    const cats: Category[] = [];
    for (const m of missed) if (!cats.includes(m.category)) cats.push(m.category);
    return {
      score: this.score,
      correct,
      incorrect: total - correct,
      total,
      accuracy,
      rank: rankFor(accuracy),
      passed: passes(accuracy),
      retries: this.retries,
      missed,
      mistakes: cats.map((c) => ({ ...CATEGORY_LESSON[c] })),
    };
  }
}
